import { Injectable } from "@angular/core";
import { ProjectService, WmHttpService } from "@wms/app/services";
import { environment } from "../../../environments/environment";
import { Subscription } from "rxjs";
import { IPageMinJSON } from "@wms/design-container";
import {get} from "lodash-es";

interface PrefabDataParams {
  type: string;
  prefabName: string;
  pageNameInPrefab?: string;
}

interface PrefabData {
  config: Object;
  properties: Object;
  pages: Map<string, IPageMinJSON>
}

@Injectable({
  providedIn: 'root'
})
export class PrefabCacheService {
  public prefabConfigCache: {[key: string]: PrefabData};
  public activePageName: string;
  subscription: Subscription = new Subscription();

  private TARGET: string = "PrefabData";
  private API_META: any = {
    getPagePrefabData: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/pages/:pageName/prefabs-data',
      method: 'GET'
    },
    getPrefabData: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/prefabs/:prefabName/data',
      method: 'GET'
    }
  }

  constructor(private wmHttpService: WmHttpService, private projectService: ProjectService) {
    this.wmHttpService.registerApiMeta(this.TARGET, this.API_META);
  }

  // This method returns the details of all the prefabs that are contained in the page.
  // This returns max 5 partial page details inside the prefab and if partial pages are more than 5 then just Main page is returned and no partials.
  // In this case, to get all partials inside prefab we can use getDataForPrefab method.
  public loadPagePrefabData(pageName: string) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "getPagePrefabData",
      urlParams: {
        pageName: pageName,
        projectID: this.projectService.getProjectId()
      }
    }) as Promise<any>).then((response: {[key: string]: PrefabData}) => {
      this.prefabConfigCache = response;
    })
  }

  // This method is used to fetch the complete details of prefab like config, props, all the page details in the prefab.
  private getDataForPrefab(prefabName: string): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: "getPrefabData",
      urlParams: {
        prefabName: prefabName,
        projectID: this.projectService.getProjectId()
      }
    }) as Promise<any>).then((response: PrefabData) => {
      this.prefabConfigCache[prefabName] = response;
      return response;
    });
  }


  // This method returns the prefab data based on the type. For suppose, when type is properties, it returns the prefab props and
  // similarly, type is config, it returns the prefab config details
  // type is page, returns the Object containing the pageName as key and its page.min.json data as its value
  public getPrefabDataByType({type, prefabName, pageNameInPrefab}: PrefabDataParams): Promise<any> {
    return new Promise((resolve, reject) => {
      let prefabData = this.prefabConfigCache[prefabName];
      let $promise: Promise<PrefabData | undefined> = Promise.resolve(prefabData);
      if (!prefabData) {
        // when prefab data inside the app page details is not found then make a call to get the prefabData and save it to cache.
        $promise = this.getDataForPrefab(prefabName);
      }
      return $promise.then((prefabData: PrefabData | undefined) => {
        if (prefabData) {
          const prefabDataType = get(prefabData, type);
          if (type === "pages" && pageNameInPrefab) {
            let data = get(prefabDataType, pageNameInPrefab);
            if (!data) {
              return this.getDataForPrefab(prefabName).then((response: PrefabData) => {
                let pageContent = get(get(response, type), pageNameInPrefab);
                if (pageContent) {
                  return resolve(pageContent);
                }
                return resolve('');
              });
            }
            return resolve(data);
          } else {
            return resolve(prefabDataType);
          }
        }
        return resolve('');
      });
    });
  }
}
